/**
 * Presentation-only formatting helpers. Never mutate or recompute domain
 * values: every distance/speed/elevation stays in SI units (metres, m/s)
 * everywhere in the store, the edit history and every export -- the
 * metric/imperial choice (`UnitSystem`, the persisted `ui.units`
 * preference) is applied only here, at display time.
 */

/** Display unit system. `'metric'` (km, m, km/h, /km) is the default. */
export type UnitSystem = 'metric' | 'imperial'

export const DEFAULT_UNIT_SYSTEM: UnitSystem = 'metric'

/** Exact by definition (international mile / foot). */
export const METERS_PER_MILE = 1609.344
export const METERS_PER_FOOT = 0.3048
const METERS_PER_KM = 1000

// ---------------------------------------------------------------------------
// Unit conversions (numbers in, numbers out) -- for charts and inputs that
// need the converted value itself rather than a formatted string.
// ---------------------------------------------------------------------------

/** Metres -> km (metric) or mi (imperial). */
export function metersToDistanceUnit(meters: number, units: UnitSystem): number {
  return meters / (units === 'imperial' ? METERS_PER_MILE : METERS_PER_KM)
}

/** Metres -> m (metric) or ft (imperial); for elevation, ascent, descent. */
export function metersToElevationUnit(meters: number, units: UnitSystem): number {
  return units === 'imperial' ? meters / METERS_PER_FOOT : meters
}

/** m/s -> km/h (metric) or mph (imperial). */
export function metersPerSecondToSpeedUnit(metersPerSecond: number, units: UnitSystem): number {
  return units === 'imperial'
    ? (metersPerSecond * 3600) / METERS_PER_MILE
    : (metersPerSecond * 3600) / METERS_PER_KM
}

/** Seconds per km -> seconds per km (metric) or per mile (imperial). */
export function secPerKmToPaceUnit(secPerKm: number, units: UnitSystem): number {
  return units === 'imperial' ? secPerKm * (METERS_PER_MILE / METERS_PER_KM) : secPerKm
}

export function distanceUnitLabel(units: UnitSystem): 'km' | 'mi' {
  return units === 'imperial' ? 'mi' : 'km'
}

export function elevationUnitLabel(units: UnitSystem): 'm' | 'ft' {
  return units === 'imperial' ? 'ft' : 'm'
}

export function speedUnitLabel(units: UnitSystem): 'km/h' | 'mph' {
  return units === 'imperial' ? 'mph' : 'km/h'
}

export function paceUnitLabel(units: UnitSystem): '/km' | '/mi' {
  return units === 'imperial' ? '/mi' : '/km'
}

// ---------------------------------------------------------------------------
// Formatters. `units` defaults to metric so unit-agnostic callers keep
// working; anything user-facing should pass the current preference
// (`useUnits()` / `useUnitFormatters()` in `hooks/use-units.ts`).
// ---------------------------------------------------------------------------

export function formatDurationSeconds(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return 'Unknown'
  const totalWhole = Math.round(totalSeconds)
  const hours = Math.floor(totalWhole / 3600)
  const minutes = Math.floor((totalWhole % 3600) / 60)
  const seconds = totalWhole % 60
  const pad = (n: number): string => n.toString().padStart(2, '0')
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
}

export function formatDistanceMeters(meters: number, units: UnitSystem = 'metric'): string {
  return `${metersToDistanceUnit(meters, units).toFixed(2)} ${distanceUnitLabel(units)}`
}

export function formatElevationMeters(meters: number, units: UnitSystem = 'metric'): string {
  return `${Math.round(metersToElevationUnit(meters, units))} ${elevationUnitLabel(units)}`
}

export function formatSpeedMetersPerSecond(
  metersPerSecond: number,
  units: UnitSystem = 'metric',
): string {
  return `${metersPerSecondToSpeedUnit(metersPerSecond, units).toFixed(1)} ${speedUnitLabel(units)}`
}

/** Pace from seconds per km (fit-core's unit), shown per km or per mile. */
export function formatPaceSecPerKm(secPerKm: number, units: UnitSystem = 'metric'): string {
  if (!Number.isFinite(secPerKm) || secPerKm < 0) return 'Unknown'
  // Round the total first so e.g. 359.6 s reads 6:00, never 5:60.
  const total = Math.round(secPerKmToPaceUnit(secPerKm, units))
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')} ${paceUnitLabel(units)}`
}

export function formatHeartRate(bpm: number): string {
  return `${Math.round(bpm)} bpm`
}

/** Cadence with its sport-dependent unit (`rpm` cycling, `spm` running/walking), as fit-core reports it. */
export function formatCadence(cadence: { value: number; unit: string }): string {
  return `${Math.round(cadence.value)} ${cadence.unit}`
}

export function formatCalories(kcal: number): string {
  return `${Math.round(kcal)} kcal`
}

/** The unit-dependent formatters bound to one `UnitSystem` (see `createUnitFormatters`). */
export interface UnitFormatters {
  units: UnitSystem
  distance: (meters: number) => string
  elevation: (meters: number) => string
  speed: (metersPerSecond: number) => string
  pace: (secPerKm: number) => string
}

/** Binds the unit-dependent formatters to `units`, for passing around as plain `(value) => string` callbacks. */
export function createUnitFormatters(units: UnitSystem): UnitFormatters {
  return {
    units,
    distance: (meters) => formatDistanceMeters(meters, units),
    elevation: (meters) => formatElevationMeters(meters, units),
    speed: (metersPerSecond) => formatSpeedMetersPerSecond(metersPerSecond, units),
    pace: (secPerKm) => formatPaceSecPerKm(secPerKm, units),
  }
}
